import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyRestaurant, createRestaurant, updateRestaurant, getMyOffers, createOffer, updateOffer, deleteOffer, getTowns } from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Store, Plus, Edit2, Trash2, Star, Eye, EyeOff, Upload, Image, FileText } from 'lucide-react';
import { DAYS } from '../components/DaySelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [offers, setOffers] = useState([]);
  const [towns, setTowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRestaurantForm, setShowRestaurantForm] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingMenu, setUploadingMenu] = useState(false);
  const logoInputRef = useRef(null);
  const menuInputRef = useRef(null);

  const [restaurantForm, setRestaurantForm] = useState({
    name: '', description: '', address: '', town_id: '', phone: '', cuisine_type: '', image_url: '',
    website_url: '', booking_url: '', opening_times: {}
  });

  const [offerForm, setOfferForm] = useState({
    title: '', description: '', days: [], discount_text: '', image_url: '', voucher_code: '', terms_conditions: ''
  });

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'restaurant')) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const townsRes = await getTowns();
        setTowns(townsRes.data);

        try {
          const restaurantRes = await getMyRestaurant();
          setRestaurant(restaurantRes.data);
          setRestaurantForm({
            name: restaurantRes.data.name || '',
            description: restaurantRes.data.description || '',
            address: restaurantRes.data.address || '',
            town_id: restaurantRes.data.town_id || '',
            phone: restaurantRes.data.phone || '',
            cuisine_type: restaurantRes.data.cuisine_type || '',
            image_url: restaurantRes.data.image_url || '',
            website_url: restaurantRes.data.website_url || '',
            booking_url: restaurantRes.data.booking_url || '',
            opening_times: restaurantRes.data.opening_times || {}
          });
          const offersRes = await getMyOffers();
          setOffers(offersRes.data);
        } catch (err) {
          setShowRestaurantForm(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user, authLoading, navigate]);

  const handleRestaurantSubmit = async (e) => {
    e.preventDefault();
    try {
      if (restaurant) {
        const res = await updateRestaurant(restaurant.id, restaurantForm);
        setRestaurant(res.data);
        toast.success('Restaurant updated!');
      } else {
        const res = await createRestaurant(restaurantForm);
        setRestaurant(res.data);
        toast.success('Restaurant created!');
      }
      setShowRestaurantForm(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save restaurant';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save restaurant');
    }
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    if (offerForm.days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    try {
      if (editingOffer) {
        await updateOffer(editingOffer.id, offerForm);
        toast.success('Offer updated!');
      } else {
        await createOffer(offerForm);
        toast.success('Offer created!');
      }
      const offersRes = await getMyOffers();
      setOffers(offersRes.data);
      setShowOfferForm(false);
      setEditingOffer(null);
      setOfferForm({ title: '', description: '', days: [], discount_text: '', image_url: '', voucher_code: '', terms_conditions: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save offer';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save offer');
    }
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferForm({
      title: offer.title || '', description: offer.description || '',
      days: offer.days || [], discount_text: offer.discount_text || '', image_url: offer.image_url || '',
      voucher_code: offer.voucher_code || '', terms_conditions: offer.terms_conditions || ''
    });
    setShowOfferForm(true);
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      await deleteOffer(offerId);
      setOffers(offers.filter(o => o.id !== offerId));
      toast.success('Offer deleted!');
    } catch (error) {
      toast.error('Failed to delete offer');
    }
  };

  const handleToggleOfferActive = async (offer) => {
    try {
      await updateOffer(offer.id, { is_active: !offer.is_active });
      const offersRes = await getMyOffers();
      setOffers(offersRes.data);
      toast.success(`Offer ${offer.is_active ? 'deactivated' : 'activated'}!`);
    } catch (error) {
      toast.error('Failed to update offer');
    }
  };

  const handleDayToggle = (day) => {
    setOfferForm(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day]
    }));
  };

  const handleOpeningTimeChange = (day, value) => {
    setRestaurantForm(prev => ({
      ...prev,
      opening_times: { ...prev.opening_times, [day]: value }
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/api/upload/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded!');
      // Refresh restaurant data
      const res = await getMyRestaurant();
      setRestaurant(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload logo';
      toast.error(typeof msg === 'string' ? msg : 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleMenuUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingMenu(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/api/upload/menu`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Menu uploaded!');
      const res = await getMyRestaurant();
      setRestaurant(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload menu';
      toast.error(typeof msg === 'string' ? msg : 'Failed to upload menu');
    } finally {
      setUploadingMenu(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#7B2CBF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="dashboard-page">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#242E30]">Dashboard</h1>
            {restaurant && <p className="text-[#595959] text-sm">{restaurant.name}</p>}
          </div>
          {restaurant && (
            <Button
              onClick={() => {
                setEditingOffer(null);
                setOfferForm({ title: '', description: '', days: [], discount_text: '', image_url: '', voucher_code: '', terms_conditions: '' });
                setShowOfferForm(true);
              }}
              className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
              data-testid="add-offer-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add offer
            </Button>
          )}
        </div>

        <Tabs defaultValue="offers" className="space-y-6">
          <TabsList className="bg-white border border-[#E8E8E8] p-1 rounded-lg">
            <TabsTrigger value="offers" className="rounded-md data-[state=active]:bg-[#7B2CBF] data-[state=active]:text-white" data-testid="tab-offers">Offers</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-md data-[state=active]:bg-[#7B2CBF] data-[state=active]:text-white" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="uploads" className="rounded-md data-[state=active]:bg-[#7B2CBF] data-[state=active]:text-white" data-testid="tab-uploads">Logo & Menu</TabsTrigger>
          </TabsList>

          {/* Offers Tab */}
          <TabsContent value="offers">
            {!restaurant ? (
              <div className="bg-white rounded-xl p-8 text-center border border-[#E8E8E8]">
                <Store className="h-14 w-14 text-[#D4D4D4] mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#242E30] mb-2">Create your restaurant first</h3>
                <p className="text-[#595959] text-sm mb-4">Set up your restaurant profile before adding offers.</p>
                <Button onClick={() => setShowRestaurantForm(true)} className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white">Create restaurant</Button>
              </div>
            ) : offers.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center border border-[#E8E8E8]">
                <Plus className="h-14 w-14 text-[#D4D4D4] mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#242E30] mb-2">No offers yet</h3>
                <p className="text-[#595959] text-sm mb-4">Create your first deal to attract customers.</p>
                <Button onClick={() => setShowOfferForm(true)} className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"><Plus className="h-5 w-5 mr-2" />Add first offer</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {offers.map((offer) => (
                  <div key={offer.id} className={`bg-white border rounded-xl p-4 ${offer.is_active ? 'border-[#E8E8E8]' : 'border-red-200 bg-red-50'}`} data-testid={`offer-card-${offer.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-bold text-[#242E30] text-sm line-clamp-1">{offer.title}</h3>
                        <span className="text-xs text-[#7B2CBF] font-semibold">{offer.discount_text}</span>
                        {offer.voucher_code && <span className="text-xs text-[#595959] ml-2">Code: {offer.voucher_code}</span>}
                      </div>
                      {offer.avg_rating > 0 && (
                        <span className="flex items-center gap-1 text-xs text-[#242E30] font-medium">
                          <Star className="h-3 w-3 fill-[#FFB800] text-[#FFB800]" />{offer.avg_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#595959] mb-2 line-clamp-2">{offer.description}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(offer.days || []).map(day => (
                        <span key={day} className="text-xs bg-[#F5F5F5] text-[#242E30] px-2 py-0.5 rounded">{day.slice(0, 3)}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-[#E8E8E8]">
                      <button onClick={() => handleToggleOfferActive(offer)} className={`flex items-center gap-1 text-xs font-medium ${offer.is_active ? 'text-green-600' : 'text-red-500'}`} data-testid={`toggle-offer-${offer.id}`}>
                        {offer.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEditOffer(offer)} className="p-1.5 text-[#595959] hover:text-[#7B2CBF]" data-testid={`edit-offer-${offer.id}`}><Edit2 className="h-4 w-4" /></button>
                        <button onClick={() => handleDeleteOffer(offer.id)} className="p-1.5 text-[#595959] hover:text-red-500" data-testid={`delete-offer-${offer.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-white rounded-xl p-6 border border-[#E8E8E8]">
              {restaurant ? (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-bold text-[#242E30]">Restaurant details</h2>
                    <Button variant="outline" onClick={() => setShowRestaurantForm(true)} className="text-sm" data-testid="edit-profile-btn"><Edit2 className="h-4 w-4 mr-2" />Edit</Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div><Label className="text-[#595959] text-xs">Name</Label><p className="text-[#242E30] font-medium">{restaurant.name}</p></div>
                    <div><Label className="text-[#595959] text-xs">Cuisine</Label><p className="text-[#242E30] font-medium">{restaurant.cuisine_type || '-'}</p></div>
                    <div><Label className="text-[#595959] text-xs">Address</Label><p className="text-[#242E30]">{restaurant.address}</p></div>
                    <div><Label className="text-[#595959] text-xs">Phone</Label><p className="text-[#242E30]">{restaurant.phone || '-'}</p></div>
                    <div><Label className="text-[#595959] text-xs">Website</Label><p className="text-[#242E30]">{restaurant.website_url || '-'}</p></div>
                    <div><Label className="text-[#595959] text-xs">Booking URL</Label><p className="text-[#242E30]">{restaurant.booking_url || '-'}</p></div>
                    <div className="md:col-span-2"><Label className="text-[#595959] text-xs">Description</Label><p className="text-[#242E30]">{restaurant.description}</p></div>
                  </div>
                </div>
              ) : (
                <p className="text-[#595959] text-center py-4">No restaurant profile yet.</p>
              )}
            </div>
          </TabsContent>

          {/* Uploads Tab */}
          <TabsContent value="uploads">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div className="bg-white rounded-xl p-6 border border-[#E8E8E8]">
                <h3 className="font-bold text-[#242E30] mb-4 flex items-center gap-2"><Image className="h-5 w-5 text-[#7B2CBF]" />Restaurant Logo</h3>
                {restaurant?.logo_path && (
                  <div className="mb-4">
                    <img src={`${API_URL}/api/files/${restaurant.logo_path}`} alt="Logo" className="w-24 h-24 object-contain rounded-lg border border-[#E8E8E8] p-2" />
                  </div>
                )}
                <input type="file" accept="image/*" ref={logoInputRef} onChange={handleLogoUpload} className="hidden" />
                <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo || !restaurant} className="w-full">
                  <Upload className="h-4 w-4 mr-2" />{uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <p className="text-xs text-[#595959] mt-2">JPG, PNG, WEBP or GIF. Max 5MB.</p>
              </div>

              {/* Menu Upload */}
              <div className="bg-white rounded-xl p-6 border border-[#E8E8E8]">
                <h3 className="font-bold text-[#242E30] mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-[#7B2CBF]" />Menu PDF</h3>
                {restaurant?.menu_pdf_path && (
                  <div className="mb-4">
                    <a href={`${API_URL}/api/files/${restaurant.menu_pdf_path}`} target="_blank" rel="noopener noreferrer" className="text-[#7B2CBF] hover:underline text-sm flex items-center gap-1">
                      <FileText className="h-4 w-4" />View current menu
                    </a>
                  </div>
                )}
                <input type="file" accept="application/pdf" ref={menuInputRef} onChange={handleMenuUpload} className="hidden" />
                <Button variant="outline" onClick={() => menuInputRef.current?.click()} disabled={uploadingMenu || !restaurant} className="w-full">
                  <Upload className="h-4 w-4 mr-2" />{uploadingMenu ? 'Uploading...' : 'Upload Menu'}
                </Button>
                <p className="text-xs text-[#595959] mt-2">PDF only. Max 10MB.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Restaurant Form Dialog */}
        <Dialog open={showRestaurantForm} onOpenChange={setShowRestaurantForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{restaurant ? 'Edit restaurant' : 'Create restaurant'}</DialogTitle></DialogHeader>
            <form onSubmit={handleRestaurantSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-sm">Name *</Label><Input value={restaurantForm.name} onChange={(e) => setRestaurantForm(f => ({ ...f, name: e.target.value }))} required data-testid="restaurant-name-input" /></div>
                <div><Label className="text-sm">Cuisine</Label><Input value={restaurantForm.cuisine_type} onChange={(e) => setRestaurantForm(f => ({ ...f, cuisine_type: e.target.value }))} placeholder="e.g., Italian" data-testid="restaurant-cuisine-input" /></div>
              </div>
              <div><Label className="text-sm">Description *</Label><Textarea value={restaurantForm.description} onChange={(e) => setRestaurantForm(f => ({ ...f, description: e.target.value }))} required data-testid="restaurant-desc-input" /></div>
              <div><Label className="text-sm">Town *</Label>
                <Select value={restaurantForm.town_id} onValueChange={(val) => setRestaurantForm(f => ({ ...f, town_id: val }))}>
                  <SelectTrigger data-testid="restaurant-town-select"><SelectValue placeholder="Select town" /></SelectTrigger>
                  <SelectContent>{towns.map((town) => (<SelectItem key={town.id} value={town.id}>{town.name}{town.region && `, ${town.region}`}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-sm">Address *</Label><Input value={restaurantForm.address} onChange={(e) => setRestaurantForm(f => ({ ...f, address: e.target.value }))} required data-testid="restaurant-address-input" /></div>
                <div><Label className="text-sm">Phone</Label><Input value={restaurantForm.phone} onChange={(e) => setRestaurantForm(f => ({ ...f, phone: e.target.value }))} data-testid="restaurant-phone-input" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-sm">Website URL</Label><Input value={restaurantForm.website_url} onChange={(e) => setRestaurantForm(f => ({ ...f, website_url: e.target.value }))} placeholder="https://www.yoursite.com" /></div>
                <div><Label className="text-sm">Booking URL</Label><Input value={restaurantForm.booking_url} onChange={(e) => setRestaurantForm(f => ({ ...f, booking_url: e.target.value }))} placeholder="https://book.yoursite.com" /></div>
              </div>
              <div><Label className="text-sm">Cover Image URL</Label><Input value={restaurantForm.image_url} onChange={(e) => setRestaurantForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." /></div>
              
              {/* Opening Times */}
              <div>
                <Label className="text-sm mb-2 block">Opening Times</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {DAYS.map((day) => (
                    <div key={day.value}>
                      <Label className="text-xs text-[#595959] capitalize">{day.short}</Label>
                      <Input 
                        value={restaurantForm.opening_times[day.value] || ''} 
                        onChange={(e) => handleOpeningTimeChange(day.value, e.target.value)}
                        placeholder="9:00-22:00"
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowRestaurantForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white" data-testid="save-restaurant-btn">{restaurant ? 'Save' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Offer Form Dialog */}
        <Dialog open={showOfferForm} onOpenChange={(open) => { setShowOfferForm(open); if (!open) { setEditingOffer(null); setOfferForm({ title: '', description: '', days: [], discount_text: '', image_url: '', voucher_code: '', terms_conditions: '' }); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingOffer ? 'Edit offer' : 'Create offer'}</DialogTitle></DialogHeader>
            <form onSubmit={handleOfferSubmit} className="space-y-4">
              <div><Label className="text-sm">Title *</Label><Input value={offerForm.title} onChange={(e) => setOfferForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g., 2-for-1 Pizza" required data-testid="offer-title-input" /></div>
              <div><Label className="text-sm">Description *</Label><Textarea value={offerForm.description} onChange={(e) => setOfferForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe your offer..." required data-testid="offer-desc-input" /></div>
              <div><Label className="text-sm">Discount text *</Label><Input value={offerForm.discount_text} onChange={(e) => setOfferForm(f => ({ ...f, discount_text: e.target.value }))} placeholder="e.g., 50% OFF" required data-testid="offer-discount-input" /></div>
              <div><Label className="text-sm">Voucher Code (optional)</Label><Input value={offerForm.voucher_code} onChange={(e) => setOfferForm(f => ({ ...f, voucher_code: e.target.value }))} placeholder="e.g., PIZZA50" /><p className="text-xs text-[#595959] mt-1">Only logged-in users can see this</p></div>
              <div>
                <Label className="text-sm mb-2 block">Days *</Label>
                <div className="grid grid-cols-4 gap-2">
                  {DAYS.map((day) => (
                    <label key={day.value} className={`flex items-center justify-center p-2 border rounded-lg cursor-pointer text-sm font-medium transition-colors ${offerForm.days.includes(day.value) ? 'bg-[#7B2CBF] text-white border-[#7B2CBF]' : 'border-[#E8E8E8] hover:border-[#7B2CBF]'}`}>
                      <Checkbox checked={offerForm.days.includes(day.value)} onCheckedChange={() => handleDayToggle(day.value)} className="sr-only" />{day.short}
                    </label>
                  ))}
                </div>
              </div>
              <div><Label className="text-sm">Terms & Conditions (optional)</Label><Textarea value={offerForm.terms_conditions} onChange={(e) => setOfferForm(f => ({ ...f, terms_conditions: e.target.value }))} placeholder="e.g., Valid for dine-in only. Cannot be combined with other offers." rows={3} /></div>
              <div><Label className="text-sm">Image URL</Label><Input value={offerForm.image_url} onChange={(e) => setOfferForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." /></div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowOfferForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white" data-testid="save-offer-btn">{editingOffer ? 'Save' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardPage;
